import React, { useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { seoConfig, getPersonStructuredData, getWebsiteStructuredData, getBreadcrumbStructuredData } from '@/config/seoConfig';
import { useLanguageContext } from '@/context/LanguageContext';

const SEO = ({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = 'website',
  noindex = false,
  children 
}) => {
  const { language } = useLanguageContext();
  
  // Update HTML lang attribute dynamically
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  // Fallbacks to default values
  const seoTitle = title || seoConfig.defaultTitle;
  const seoDescription = description || seoConfig.defaultDescription;
  const seoKeywords = keywords || seoConfig.defaultKeywords;
  const seoImage = image || `${seoConfig.siteUrl}${seoConfig.defaultImage}`;
  const seoUrl = url || seoConfig.siteUrl;

  // Ensure image URL is absolute
  const absoluteImageUrl = seoImage.startsWith('http') ? seoImage : `${seoConfig.siteUrl}${seoImage}`;
  
  // Dynamic locale and alternate URLs
  const currentLocale = language === 'pt' ? 'pt_PT' : 'en_US';
  const alternateLocale = language === 'pt' ? 'en_US' : 'pt_PT';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content={seoConfig.author} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={seoUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:locale" content={currentLocale} />
      <meta property="og:locale:alternate" content={alternateLocale} />
      
      {/* Hreflang for international SEO */}
      <link rel="alternate" hrefLang="pt" href={seoUrl} />
      <link rel="alternate" hrefLang="en" href={seoUrl} />
      <link rel="alternate" hrefLang="x-default" href={seoUrl} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      <meta name="twitter:creator" content="@joaoxavier" />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />

      {/* Structured Data - Person */}
      <script type="application/ld+json">
        {JSON.stringify(getPersonStructuredData())}
      </script>

      {/* Structured Data - Website */}
      <script type="application/ld+json">
        {JSON.stringify(getWebsiteStructuredData())}
      </script>
      
      {/* Structured Data - Breadcrumbs */}
      <script type="application/ld+json">
        {JSON.stringify(getBreadcrumbStructuredData(url || '/'))}
      </script>

      {/* Additional children elements */}
      {children}
    </Helmet>
  );
};

// HOC to wrap app with HelmetProvider
export const SEOProvider = ({ children }) => {
  return (
    <HelmetProvider>
      {children}
    </HelmetProvider>
  );
};

export default SEO;